export const MIN_FONT_SIZE = 6;
export const ITALIC_SKEW_DEG = 14;

/**
 * 记录在 `layer.extraInfo` 上的引擎排版标记 key。
 *
 * 值为 `true` 表示采用新引擎排版规则，无需旧 DOM 文字框兼容，不代表已经完成测量。
 * 新建入口直接写入；旧文字由 `../utils/legacy-frame` 校准后写入。
 */
export const USE_ENGINE_KEY = 'useEngine';

export const DEFAULT_LINE_HEIGHT_FACTOR = 1.4;
export const DEFAULT_FONT_COLOR = '#000000ff';

/**
 * 各语言新建文本图层的默认文案
 */
export const DEFAULT_TEXT_MAP: Record<string, string> = {
    en: 'New Text',
    pt: 'Novo texto',
    es: 'Texto nuevo',
    de: 'Neuer Text',
    'zh-Hans': '双击编辑文字',
    'zh-Hant': '雙擊編輯文字',
    id: 'Teks Baru',
    th: 'ข้อความใหม่',
    it: 'Nuovo testo',
    fr: 'Nouveau texte',
    ja: '新しいテキスト',
    ko: '새 텍스트',
    vi: 'Văn bản mới',
};

/**
 * 简体中文新建文本图层的默认文案
 */
export const DEFAULT_TEXT_ZH = DEFAULT_TEXT_MAP['zh-Hans'];
/**
 * 英文及未知语言新建文本图层的默认文案
 */
export const DEFAULT_TEXT_EN = DEFAULT_TEXT_MAP.en;

/**
 * 按当前语言返回新建文本图层的默认文案
 *
 * @param locale - 当前语言标识；未传时使用简体中文，未知语言回退英文
 * @returns 对应语言的新建文本默认文案
 */
export const getDefaultTextByLocale = (locale?: string) => {
    if (!locale) return DEFAULT_TEXT_ZH;
    return DEFAULT_TEXT_MAP[locale] ?? DEFAULT_TEXT_EN;
};

export const DEFAULT_TEXT_GRADIENT = { colors: [], angle: 0 };

/**
 * Text SVG Preview 插件与文字编辑、图层宿主及正式渲染链路之间的事件。
 */
export const Events = {
    /**
     * 请求可选 SVG Preview 插件开始、刷新或提交一次连续预览会话。
     *
     * 发送方为 Text Layer API、Behavior、Text Editor 或宿主失效处理；接收方为
     * TextLayerSVGPreviewPlugin。参数为 SVGPreviewRequest，包含 phase、目标 layer 及可选 snapshot。
     */
    SVGPreviewRequest: 'text-layer/svg-preview-request',
    /**
     * 通知 Text Layer 宿主 SVG Preview 的接管状态已经变化。
     *
     * 发送方为 TextLayerSVGPreviewPlugin，接收方为对应 layerId 的 EdgelessTextLayer。
     * 参数依次为 layerId 和 active；true 仅在有效 Preview 已建立后发送，false 在 Preview 释放时发送。
     */
    SVGPreviewActiveChange: 'text-layer/svg-preview-active-change',
    /**
     * 通知 SVG Preview 插件最新正式 drawable 已通过版本校验并完成交接。
     *
     * 发送方为 EdgelessTextLayer，接收方为 TextLayerSVGPreviewPlugin。参数依次为 layerId 和 ready；
     * ready 为 false 表示正式资源构建失败，插件释放 Preview 后需要恢复旧 Canvas 画面。
     */
    DrawableSettled: 'text-layer/drawable-settled',
} as const;
